import React, { useState, useEffect, useCallback } from "react";
import { supabase } from "../../supabase";
import Navbar from "../Navbar/Navbar";
import "./Messages.css";

const Messages = () => {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState(null);
  const [relatedBooking, setRelatedBooking] = useState(null);
  const [showBookingPanel, setShowBookingPanel] = useState(false);
  const [filter, setFilter] = useState("all");
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showConversations, setShowConversations] = useState(true);

  // Memoize fetchConversations with useCallback
  const fetchConversations = useCallback(async () => {
    if (!userId) return;

    try {
      const { data, error } = await supabase
        .from("conversations")
        .select("*")
        .or(`participant_one_id.eq.${userId},participant_two_id.eq.${userId}`)
        .order("last_message_at", { ascending: false });

      if (error) throw error;

      // Get other participant info and last message for each conversation
      const conversationsWithUsers = await Promise.all(
        data.map(async (conv) => {
          const otherUserId =
            conv.participant_one_id === userId
              ? conv.participant_two_id
              : conv.participant_one_id;

          const { data: userData } = await supabase
            .from("user_profiles")
            .select("user_id, full_name, avatar_url")
            .eq("user_id", otherUserId)
            .single();

          // Get last message
          const { data: lastMessage } = await supabase
            .from("messages")
            .select("content, created_at, sender_id")
            .or(
              `and(sender_id.eq.${userId},receiver_id.eq.${otherUserId}),and(sender_id.eq.${otherUserId},receiver_id.eq.${userId})`
            )
            .order("created_at", { ascending: false })
            .limit(1)
            .single();

          return {
            ...conv,
            otherUser: userData || {
              user_id: otherUserId,
              full_name: "Unknown User",
              avatar_url: null,
            },
            lastMessage: lastMessage || null,
          };
        })
      );

      setConversations(conversationsWithUsers);
    } catch (error) {
      console.error("Error fetching conversations:", error);
    }
  }, [userId]); // Include userId as dependency

  useEffect(() => {
    initializeUser();
  }, []);

  useEffect(() => {
    if (userId) {
      fetchConversations();
    }
  }, [userId, fetchConversations]); // Now include fetchConversations

  useEffect(() => {
    if (selectedConversation && userId) {
      fetchMessages();
      fetchRelatedBooking();
      // On mobile, switch to chat view when conversation is selected
      if (isMobile) {
        setShowConversations(false);
      }
    }
  }, [selectedConversation, userId, isMobile]);

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      // Reset to conversations view when switching to mobile
      if (mobile && !selectedConversation) {
        setShowConversations(true);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [selectedConversation]);

  const initializeUser = async () => {
    try {
      const { data, error } = await supabase.auth.getUser();
      if (error) throw error;

      if (data?.user) {
        setUserId(data.user.id);
      }
    } catch (error) {
      console.error("Error getting user:", error);
    }
    setLoading(false);
  };

  const fetchMessages = async () => {
    if (!selectedConversation) return;

    try {
      const otherUserId = selectedConversation.otherUser.user_id;

      const { data, error } = await supabase
        .from("messages")
        .select("*")
        .or(
          `and(sender_id.eq.${userId},receiver_id.eq.${otherUserId}),and(sender_id.eq.${otherUserId},receiver_id.eq.${userId})`
        )
        .order("created_at", { ascending: true });

      if (error) throw error;
      setMessages(data || []);

      // Mark all unread messages from the other user as read
      await supabase
        .from("messages")
        .update({ read: true })
        .eq("sender_id", otherUserId)
        .eq("receiver_id", userId)
        .eq("read", false);
    } catch (error) {
      console.error("Error fetching messages:", error);
    }
  };

  const fetchRelatedBooking = async () => {
    if (!selectedConversation) return;

    try {
      const otherUserId = selectedConversation.otherUser.user_id;

      const { data, error } = await supabase
        .from("booking_requests")
        .select(
          `
          *,
          sender:sender_id(user_id, full_name, avatar_url),
          receiver:receiver_id(user_id, full_name, avatar_url)
        `
        )
        .or(
          `and(sender_id.eq.${userId},receiver_id.eq.${otherUserId}),and(sender_id.eq.${otherUserId},receiver_id.eq.${userId})`
        )
        .order("created_at", { ascending: false })
        .limit(1);

      if (error) throw error;

      if (data && data.length > 0) {
        setRelatedBooking(data[0]);
        setShowBookingPanel(true); // Auto-show if booking exists
      } else {
        setRelatedBooking(null);
        setShowBookingPanel(false);
      }
    } catch (error) {
      console.error("Error fetching related booking:", error);
      setRelatedBooking(null);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return;

    try {
      const { error } = await supabase.from("messages").insert({
        sender_id: userId,
        receiver_id: selectedConversation.otherUser.user_id,
        content: newMessage.trim(),
      });

      if (error) throw error;

      await supabase
        .from("conversations")
        .update({ last_message_at: new Date().toISOString() })
        .eq("id", selectedConversation.id);

      setNewMessage("");
      fetchMessages();
      fetchConversations();
    } catch (error) {
      console.error("Error sending message:", error);
    }
  };

  const formatEventDateTime = (startDateTime, endDateTime, timezone) => {
    if (!startDateTime || !endDateTime) return "Date/Time not specified";

    const start = new Date(startDateTime);
    const end = new Date(endDateTime);

    const startDate = start.toLocaleDateString();
    const startTime = start.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
    const endTime = end.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });

    return `${startDate} • ${startTime} - ${endTime}`;
  };

  const getBookingStatusColor = (status) => {
    switch (status) {
      case "pending":
        return "#ffc107";
      case "accepted":
        return "#28a745";
      case "rejected":
        return "#dc3545";
      default:
        return "#6c757d";
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);
    const diffInDays = diffInHours / 24;

    if (diffInHours < 24) {
      return date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    } else if (diffInDays < 7) {
      return date.toLocaleDateString([], { weekday: "short" });
    } else {
      return date.toLocaleDateString([], { month: "short", day: "numeric" });
    }
  };

  const formatMessageTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString([], { month: "short", day: "numeric" });
  };

  const truncateMessage = (message, maxLength = 50) => {
    if (!message) return "";
    return message.length > maxLength
      ? message.substring(0, maxLength) + "..."
      : message;
  };

  // Mobile navigation functions
  const handleConversationSelect = (conversation) => {
    setSelectedConversation(conversation);
    if (isMobile) {
      setShowConversations(false);
    }
  };

  const handleBackToConversations = () => {
    if (isMobile) {
      setShowConversations(true);
      setSelectedConversation(null);
    }
  };

  const handleShowBookingPanel = () => {
    setShowBookingPanel(true);
  };

  if (loading) {
    return <div className="messages-loading">Loading messages...</div>;
  }

  return (
    <>
      <Navbar />
      <div className="airbnb-messages-container">
      {/* Left Sidebar - Hidden on mobile when chat is open */}
      <div
        className={`airbnb-messages-sidebar ${isMobile && !showConversations ? "mobile-hidden" : ""}`}
      >
        <div className="airbnb-sidebar-header">
          <h1>Messages</h1>
          <div className="airbnb-filter-buttons">
            <button
              className={`airbnb-filter-btn ${filter === "all" ? "active" : ""}`}
              onClick={() => setFilter("all")}
            >
              All
            </button>
            <button
              className={`airbnb-filter-btn ${filter === "unread" ? "active" : ""}`}
              onClick={() => setFilter("unread")}
            >
              Unread
            </button>
          </div>
        </div>

        <div className="airbnb-conversations-list">
          {conversations.map((conv) => (
            <div
              key={conv.id}
              className={`airbnb-conversation-item ${selectedConversation?.id === conv.id ? "selected" : ""}`}
              onClick={() => handleConversationSelect(conv)}
            >
              <div className="airbnb-conversation-avatars">
                <img
                  src={conv.otherUser?.avatar_url || "/default-avatar.png"}
                  alt={conv.otherUser?.full_name}
                  className="airbnb-conversation-avatar"
                  onError={(e) => {
                    e.target.src = "/default-avatar.png";
                  }}
                />
              </div>
              <div className="airbnb-conversation-content">
                <div className="airbnb-conversation-header">
                  <span className="airbnb-conversation-name">
                    {conv.otherUser?.full_name || "Unknown User"}
                  </span>
                  <span className="airbnb-conversation-date">
                    {formatTime(conv.last_message_at)}
                  </span>
                </div>
                <div className="airbnb-conversation-preview">
                  {conv.lastMessage && (
                    <span className="airbnb-message-preview">
                      {conv.lastMessage.sender_id === userId ? "You: " : ""}
                      {truncateMessage(conv.lastMessage.content)}
                    </span>
                  )}
                </div>
              </div>
              {isMobile && (
                <svg
                  className="mobile-chevron"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <polyline points="9,18 15,12 9,6" />
                </svg>
              )}
            </div>
          ))}

          {conversations.length === 0 && (
            <div className="airbnb-no-conversations">No conversations yet.</div>
          )}
        </div>
      </div>

      {/* Center Chat Area - Hidden on mobile when conversations are shown */}
      <div
        className={`airbnb-chat-area ${isMobile && showConversations ? "mobile-hidden" : ""}`}
      >
        {selectedConversation ? (
          <>
            {/* Chat Header with back button on mobile */}
            <div className="airbnb-chat-header">
              {isMobile && (
                <button
                  className="mobile-back-button"
                  onClick={handleBackToConversations}
                  aria-label="Back to conversations"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <polyline points="15,18 9,12 15,6" />
                  </svg>
                </button>
              )}
              <div className="airbnb-chat-user-info">
                <div className="airbnb-chat-avatars">
                  <img
                    src={
                      selectedConversation.otherUser?.avatar_url ||
                      "/default-avatar.png"
                    }
                    alt={selectedConversation.otherUser?.full_name}
                    className="airbnb-chat-avatar"
                    onError={(e) => {
                      e.target.src = "/default-avatar.png";
                    }}
                  />
                </div>
                <div className="airbnb-chat-user-details">
                  <h2>
                    {selectedConversation.otherUser?.full_name ||
                      "Unknown User"}
                  </h2>
                </div>
              </div>
              {/* Booking details button */}
              {relatedBooking && (
                <button
                  className="mobile-booking-button"
                  onClick={handleShowBookingPanel}
                  aria-label="Show booking details"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                  </svg>
                </button>
              )}
            </div>

            {/* Messages Area */}
            <div className="airbnb-messages-area">
              {messages.map((message, index) => {
                const isOwnMessage = message.sender_id === userId;
                const showDate =
                  index === 0 ||
                  formatMessageTime(message.created_at) !==
                    formatMessageTime(messages[index - 1].created_at);

                return (
                  <div key={message.id}>
                    {showDate && (
                      <div className="airbnb-date-separator">
                        {formatMessageTime(message.created_at)}
                      </div>
                    )}
                    <div
                      className={`airbnb-message ${isOwnMessage ? "own" : "other"}`}
                    >
                      {!isOwnMessage && (
                        <img
                          src={
                            selectedConversation.otherUser?.avatar_url ||
                            "/default-avatar.png"
                          }
                          alt={selectedConversation.otherUser?.full_name}
                          className="airbnb-message-avatar"
                          onError={(e) => {
                            e.target.src = "/default-avatar.png";
                          }}
                        />
                      )}
                      <div className="airbnb-message-content">
                        <div className="airbnb-message-bubble">
                          {message.content}
                        </div>
                        <div className="airbnb-message-time">
                          {formatTime(message.created_at)}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Message Input */}
            <div className="airbnb-message-input-area">
              <div className="airbnb-input-container">
                <input
                  type="text"
                  placeholder="Type a message..."
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && sendMessage()}
                  className="airbnb-message-input"
                />
                <button
                  onClick={sendMessage}
                  disabled={!newMessage.trim()}
                  className="airbnb-send-button"
                >
                  ↗
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="airbnb-no-chat-selected">
            <h3>Select a conversation to start messaging</h3>
          </div>
        )}
      </div>

      {/* Right Panel - Booking Details (Mobile Overlay) */}
      {showBookingPanel && relatedBooking && (
        <div
          className={`airbnb-booking-panel ${isMobile ? "mobile-overlay" : ""}`}
        >
          <div className="airbnb-booking-header">
            <h3>Booking Details</h3>
            <button
              className="airbnb-close-panel"
              onClick={() => setShowBookingPanel(false)}
            >
              ✕
            </button>
          </div>

          <div className="airbnb-booking-content">
            <div className="airbnb-booking-section">
              <h4>Booking status</h4>
              <div className="airbnb-status-badge">
                <span
                  className="airbnb-status-dot"
                  style={{
                    backgroundColor: getBookingStatusColor(
                      relatedBooking.status
                    ),
                  }}
                ></span>
                {relatedBooking.status.charAt(0).toUpperCase() +
                  relatedBooking.status.slice(1)}
              </div>
            </div>

            {relatedBooking.start_datetime && relatedBooking.end_datetime && (
              <div className="airbnb-booking-section">
                <h4>Event details</h4>
                <p className="airbnb-event-time">
                  {formatEventDateTime(
                    relatedBooking.start_datetime,
                    relatedBooking.end_datetime,
                    relatedBooking.timezone
                  )}
                </p>
                {relatedBooking.timezone && (
                  <p className="airbnb-timezone">
                    Timezone: {relatedBooking.timezone}
                  </p>
                )}
              </div>
            )}

            <div className="airbnb-booking-section">
              <h4>Request message</h4>
              <p className="airbnb-booking-message">{relatedBooking.message}</p>
            </div>

            <div className="airbnb-booking-section">
              <h4>Participants</h4>
              <div className="airbnb-participants">
                <p>
                  <strong>Requester:</strong>{" "}
                  {relatedBooking.sender?.full_name || "Unknown"}
                </p>
                <p>
                  <strong>Speaker:</strong>{" "}
                  {relatedBooking.receiver?.full_name || "Unknown"}
                </p>
              </div>
            </div>

            <div className="airbnb-booking-section">
              <p className="airbnb-booking-dates">
                <strong>Requested:</strong>{" "}
                {new Date(relatedBooking.created_at).toLocaleDateString()}
              </p>
              {relatedBooking.updated_at !== relatedBooking.created_at && (
                <p className="airbnb-booking-dates">
                  <strong>Last updated:</strong>{" "}
                  {new Date(relatedBooking.updated_at).toLocaleDateString()}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
      </div>
    </>
  );
};

export default Messages;
